import { AgreementBodyInput } from "@/app/ProDrivers/Agreements/Components/AgreementBodyInput";
import { AgreementPreview } from "@/app/ProDrivers/Agreements/Components/AgreementPreview";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";
import { useSyncedScroll } from "@/app/ProDrivers/Agreements/Hooks/useSyncedScroll";
import { cn } from "@/lib/utils";
import { useState } from "react";

type Props = {
  title: string;
  agreementId?: string | null;
  pdfUrl?: string | null;
  /** Height of both panes — dialogs need a shorter pane than a record screen. */
  heightClass?: string;
  /** Stretch both panes to the parent's height instead of a fixed one. */
  fill?: boolean;
  /** Dialogs are already constrained, so they skip the full-bleed grid. */
  fullBleed?: boolean;
  /** Off for a real agreement — its merge fields were filled in on creation. */
  showMergeFields?: boolean;
};

export function AgreementContentForm({
  title,
  agreementId,
  pdfUrl,
  heightClass = "h-[calc(100vh-14rem)]",
  fill = false,
  fullBleed = true,
  showMergeFields = true,
}: Props) {
  const { isEditing } = useFormEditState();
  // Element state, not refs — the preview iframe mounts once its render lands.
  const [editorEl, setEditorEl] = useState<HTMLDivElement | null>(null);
  const [previewEl, setPreviewEl] = useState<HTMLIFrameElement | null>(null);

  useSyncedScroll(editorEl, previewEl);

  const preview = (
    <div
      className={cn(
        "flex flex-col",
        fill && "min-h-0 flex-1",
        fullBleed && "lg:sticky lg:top-4",
      )}
    >
      <label className="mb-2 text-sm font-medium text-zinc-700">
        {isEditing ? "Live preview" : "Document"}
      </label>
      <div
        className={cn(
          "overflow-hidden rounded-md border border-zinc-200",
          fill ? "min-h-0 flex-1" : heightClass,
        )}
      >
        <AgreementPreview
          title={title}
          agreementId={agreementId}
          pdfUrl={isEditing ? null : pdfUrl}
          iframeRef={setPreviewEl}
        />
      </div>
    </div>
  );

  if (!isEditing) {
    return (
      <div className={cn("flex flex-col", fill && "min-h-0 flex-1")}>
        {preview}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 lg:grid-cols-2",
        fill ? "min-h-0 flex-1 items-stretch" : "items-start",
        fullBleed && "mx-[calc(50%-50cqw)] w-[100cqw]",
      )}
    >
      {preview}
      <div className={cn("flex flex-col", fill && "min-h-0")}>
        {fill ? (
          <label className="mb-2 text-sm font-medium text-zinc-700">
            Editor
          </label>
        ) : null}
        <AgreementBodyInput
          name="body"
          label="Editor"
          heightClass={fill ? "min-h-0 flex-1" : heightClass}
          fill={fill}
          scrollRef={setEditorEl}
          showMergeFields={showMergeFields}
        />
      </div>
    </div>
  );
}
